/**
 * Card for a field event.
 */
import * as React from 'react';
import { Calendar, Clock, Building2, UserCheck, Repeat2, FileEdit, BookOpen } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableCard } from '@/components/common/data-table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { EVENT_STATUS_CONFIG, EVENT_RECURRENCE_LABELS } from './event-configs';
import { getRowPermission } from '@/hooks/usePageActions';
import { getErrorMessage } from '@/utils/errors-utils';
import {
  type EventItem,
  formatEventTime,
  formatEventDate,
  deriveEventActivities,
  getEventActionState,
  useNavigateToEventSummary,
} from './event-table-utils';

/** Props for {@link EventCard}. */
interface EventCardProps {
  event: EventItem;
  onView?: (id: string) => void;
  onEdit?: (id: string) => void;
  onCancel?: (id: string) => void;
  onManageAttendance?: (id: string) => void;
  pageActions?: {
    canEditRow?: boolean;
    canCancelRow?: boolean;
    canManageAttendance?: boolean;
    canDeleteRow?: boolean;
    canViewSummary?: boolean;
    canEditSummary?: boolean;
  };
}

/** Card for a field event. */
export const EventCard: React.FC<EventCardProps> = ({
  event,
  onView,
  onEdit,
  onCancel,
  onManageAttendance,
  pageActions,
}) => {
  const statusConfig = EVENT_STATUS_CONFIG[event.status];
  const { navigateToSummary } = useNavigateToEventSummary();

  const canEdit = getRowPermission(event, 'action-edit-row', pageActions?.canEditRow ?? true);
  const canCancel = getRowPermission(event, 'action-cancel-row', pageActions?.canCancelRow ?? true);
  const canAttendance = getRowPermission(
    event,
    'action-manage-attendance',
    pageActions?.canManageAttendance ?? true,
  );
  const canEditSummary = pageActions?.canEditSummary ?? false;

  const { isEditable, isCancelable, isAttendanceEnabled } = getEventActionState(
    event,
    canEdit,
    canCancel,
    canAttendance,
  );

  const dateLabel = formatEventDate(event.eventDate, 'EEEE d MMMM yyyy');

  const { firstActivity, extraActivities, firstActivityTypeLabel } = deriveEventActivities(event);

  return (
    <TableCard onClick={() => onView?.(event.id)}>
      <div className="data-card-content">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <LabelBadge
              label={statusConfig.label}
              icon={
                statusConfig.isInProgress ? (
                  <span className="session-dot-pulse" />
                ) : (
                  statusConfig.icon
                )
              }
              variant={statusConfig.badgeVariant}
              color={statusConfig.badgeColor}
            />
            {event.mode === 'recurring' &&
              event.recurrenceType &&
              event.recurrenceType !== 'none' && (
                <LabelBadge
                  label={EVENT_RECURRENCE_LABELS[event.recurrenceType] || 'Récurrente'}
                  icon={Repeat2}
                  variant="outlined"
                  color="var(--color-2-nuance-1)"
                />
              )}
          </div>
        </div>

        <div className="flex flex-col gap-2 text-sm">
          <div className="flex items-center gap-2 mb-1">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)] shrink-0">
              <Calendar className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
            </div>
            <div className="flex flex-col min-w-0">
              <span className="font-medium text-foreground capitalize">{dateLabel}</span>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="w-3 h-3 text-[var(--color-1-nuance-1)]" />
                <span>
                  {formatEventTime(event.startTime)} - {formatEventTime(event.endTime)}
                </span>
              </div>
            </div>
          </div>

          {(event.establishmentName || event.establishment) && (
            <div className="flex items-start gap-2">
              <Building2
                className="w-4 h-4 shrink-0 mt-0.5"
                style={{ color: 'var(--color-1-nuance-1)' }}
              />
              <span className="text-foreground">
                {event.establishmentName || event.establishment?.name}
              </span>
            </div>
          )}

          {firstActivity && (
            <div className="flex flex-col gap-0.5">
              <div className="flex items-center gap-2 flex-wrap">
                <BookOpen
                  className="w-4 h-4 shrink-0"
                  style={{ color: 'var(--color-1-nuance-1)' }}
                />
                <span className="font-medium text-foreground">{firstActivity.label}</span>
                {extraActivities.length > 0 && (
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold cursor-default select-none bg-[color-mix(in_srgb,var(--color-4-nuance-1)_12%,transparent)] text-[var(--color-4-nuance-1)] border border-[color-mix(in_srgb,var(--color-4-nuance-1)_30%,transparent)]">
                          +{extraActivities.length}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent variant="light" side="top" className="max-w-xs">
                        <ul className="space-y-1 text-xs">
                          {extraActivities.map((a) => {
                            const t = a.activityTypeName ?? null;
                            return (
                              <li key={a.id ?? `${a.label}-${t ?? ''}`} className="flex flex-col">
                                <span className="font-medium">{a.label}</span>
                                {t && <span className="text-muted-foreground">Type : {t}</span>}
                              </li>
                            );
                          })}
                        </ul>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
              {firstActivityTypeLabel && (
                <span className="text-xs text-muted-foreground pl-6">
                  Type&nbsp;: {firstActivityTypeLabel}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="data-card-footer">
        <div className="flex gap-2">
          <Button
            preset="row-view"
            onClick={(e) => {
              e.stopPropagation();
              onView?.(event.id);
            }}
          />

          {onEdit && (
            <Button
              preset="row-edit"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(event.id);
              }}
              disabled={!isEditable}
              tooltipText={isEditable ? 'Modifier' : getErrorMessage('cannotEdit')}
            />
          )}

          {onManageAttendance && (
            <Button
              variant="ghost"
              color="#7c3aed"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                onManageAttendance(event.id);
              }}
              icon={UserCheck}
              disabled={!isAttendanceEnabled}
              tooltipText={
                isAttendanceEnabled
                  ? 'Gérer les présences'
                  : getErrorMessage('eventAttendanceUnavailable')
              }
            />
          )}

          {canEditSummary && (
            <Button
              variant="ghost"
              color="var(--color-3-nuance-1)"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                void navigateToSummary(event.id, 'edit');
              }}
              icon={FileEdit}
              tooltipText="Modifier la synthèse"
            />
          )}

          {onCancel && (
            <Button
              preset="row-cancel"
              onClick={(e) => {
                e.stopPropagation();
                onCancel(event.id);
              }}
              disabled={!isCancelable}
              tooltipText={isCancelable ? "Annuler l'événement" : getErrorMessage('cannotCancel')}
            />
          )}
        </div>
      </div>
    </TableCard>
  );
};
